import { QueryVariablePreprocessor } from './initialQueryVariableProcessors.types';
import { useCallback } from 'react';
import { QueryModel, SearchModel, SortModel } from '../DataTableQuery.types';
import { useDataTableContext } from '../../DataTableContext';
import { OnQuickReportsChanged } from '../DataTableQuery.types';

export function useQuickReportsProcessor(
  quickReports: OnQuickReportsChanged<QueryModel, SortModel>[] | undefined,
  loadingQuickReportsDone: boolean | undefined
): QueryVariablePreprocessor.Function {
  const { getAllColumns, getState } = useDataTableContext();
  const columns = getAllColumns?.();
  const state = getState?.();

  return useCallback(
    (prevResult) => {
      const result = prevResult?.result as SearchModel<any, any>;

      // if quickReports (loadingQuickReportsDone is undefined) are not specified - passes the result to the next processor
      if (typeof loadingQuickReportsDone === 'undefined') {
        return {
          result,
          status: QueryVariablePreprocessor.Status.COMPLETED
        };
      }
      const isComplete = loadingQuickReportsDone && typeof quickReports !== 'undefined';

      if (!isComplete) {
        return {
          result,
          status: QueryVariablePreprocessor.Status.IN_PROGRESS
        };
      }

      const selectedQuickReport = result?.quickReportId
        ? quickReports?.find((search) => search.quickReportId === result?.quickReportId)
        : isEmptyObject(result?.query)
          ? quickReports?.find((search) => search.default)
          : undefined;

      const columnsIds = columns?.map((column) => column.id) ?? [];

      if (!selectedQuickReport) {
        return {
          result: {
            query: result?.query,
            sort: result?.sort ?? sorted(state?.sorting, columnsIds),
            quickReportId: undefined
          },
          status: QueryVariablePreprocessor.Status.COMPLETED
        };
      }
      return {
        result: {
          ...result,
          query: selectedQuickReport.query ?? result?.query,
          sort: selectedQuickReport.sort ?? (sorted(state?.sorting, columnsIds) as SearchModel<any, any>['sort']),
          quickReportId: selectedQuickReport.quickReportId
        },
        status: QueryVariablePreprocessor.Status.COMPLETED
      };
    },
    [columns, loadingQuickReportsDone, quickReports, state?.sorting]
  );
}

const sorted = (
  sortBy:
    | {
        id: string;
        desc: boolean;
      }[]
    | undefined,
  columnsIds: string[]
) => {
  if (sortBy?.[0]?.id) {
    return sortBy
      .map((sort) => ({ type: sort.id, reversed: sort.desc }))
      .filter((sort) => columnsIds.includes(sort.type));
  }
  return [];
};

const isEmptyObject = (obj: any) => {
  if (obj === null || obj === undefined) {
    return true;
  }
  return Object.keys(obj).length === 0;
};
